import { getI18n } from 'client/i18n'
import {
  AMOUNT_TYPES,
  CROWDFUNDING_GOAL_AMOUNT,
  CROWDFUNDING_CAMPAIGN_MAX_DAYS,
} from 'shared/shared_consts'
import { CROWDFUNDING_DESCRIPTION_LENGTH, TITLE_LENGTH } from 'shared/lengths'
import EditMain from './edit_main_cf'
import {
  getTranslatedCategoryList,
  isValidCategory,
  getCategoryLabel,
  filterDepreciatedCategory,
  sortCategory,
} from 'lib/category'
import {
  combine,
  required,
  atMost,
  atLeast,
  length,
  futureDate,
} from 'lib/validators'
import { endDateValidators, maxDaysValidator } from 'lib/end_date_validators'
import { reduxForm } from 'redux-form'
import { updateRemotely } from 'redux/modules/campaign'
import formUtils from 'lib/form_utils'
import { formatMoney } from 'client/griffin'
import { show as showComingSoon } from 'redux/modules/coming_soon_dialog'
import { getCampaignSelector, isGNSelector } from 'redux/selectors/campaign'
import { show } from 'redux/modules/toast_notification'
import _get from 'lodash/get'

const i18n = getI18n('components/edit_glimpse/edit_main')
const i18n_end_date = getI18n('components/edit_glimpse/edit_end_date')

function stringifyContributionPrefs(campaign) {
  switch (campaign.amount_type) {
    case AMOUNT_TYPES.any:
      return i18n('any')
    case AMOUNT_TYPES.at_least:
      return i18n('at_least', {
        amount: formatMoney(campaign.currency, campaign.amount),
      })
    case AMOUNT_TYPES.exactly:
      return i18n('exactly', {
        amount: formatMoney(campaign.currency, campaign.amount),
      })
    default:
      return 'UNKNOWN'
  }
}

function stringifyPublicTrackingPrefs(campaign) {
  let octal =
    (campaign.share_progress ? 1 : 0) +
    (campaign.share_contributors ? 2 : 0) +
    (campaign.share_progress && campaign.share_contributions ? 4 : 0)

  return i18n('tracking_prefs_' + octal)
}

const mapStateToProps = (state, props) => {
  const campaign = getCampaignSelector(state, props)
  const startDate = campaign.start_date
    ? new Date(campaign.start_date)
    : new Date()
  const isNewCategoryEnabled = state.flags.enable_new_category
  const categoryQueryParam = _get(state, 'queryParams.category')
  const charity = campaign.charity

  let MIN_AMOUNT
  let MAX_AMOUNT
  let formattedMaxGoalAmount
  let formattedMinGoalAmount = formatMoney(
    campaign.currency,
    state.flags.min_amount_donation,
    {
      removeTrailingZeros: true,
    }
  )
  let categoryList = getTranslatedCategoryList(state)

  categoryList = filterDepreciatedCategory(categoryList, isNewCategoryEnabled)
  categoryList = sortCategory(categoryList)

  // category initial/default value
  const category =
    campaign.category ||
    (isValidCategory(state, categoryQueryParam)
      ? categoryQueryParam
      : categoryList[0].value)

  let validators = [
    required('description', i18n('description_required')),
    length(
      'description',
      CROWDFUNDING_DESCRIPTION_LENGTH,
      i18n('description_too_long', {
        maxLength: CROWDFUNDING_DESCRIPTION_LENGTH,
      })
    ),
    length('poolName', TITLE_LENGTH, i18n('pool_name_too_long')),
    required(
      'poolName',
      i18n('pool_name_is_required_cf', { maxCharLength: TITLE_LENGTH })
    ),
    required('endDate', i18n_end_date('end_date_required_cf')),
    futureDate('endDate', i18n_end_date('date_must_be_future')),
    ...endDateValidators(
      'endDate',
      i18n_end_date('invalid_date_limit_cf', {
        maxdays: CROWDFUNDING_CAMPAIGN_MAX_DAYS,
      })
    ),
    maxDaysValidator(
      'endDate',
      startDate,
      CROWDFUNDING_CAMPAIGN_MAX_DAYS,
      i18n_end_date('invalid_date_limit_cf', {
        maxdays: CROWDFUNDING_CAMPAIGN_MAX_DAYS,
      })
    ),
  ]

  if (isGNSelector(state, props)) {
    MIN_AMOUNT = CROWDFUNDING_GOAL_AMOUNT[campaign.country_code].MIN_AMOUNT
    MAX_AMOUNT = CROWDFUNDING_GOAL_AMOUNT[campaign.country_code].MAX_AMOUNT
    formattedMaxGoalAmount = formatMoney(campaign.currency, MAX_AMOUNT, {
      removeTrailingZeros: true,
    })
    formattedMinGoalAmount = formatMoney(campaign.currency, MIN_AMOUNT, {
      removeTrailingZeros: true,
    })

    validators.push(
      atMost(
        'goalAmount',
        MAX_AMOUNT,
        i18n('goal_amount_invalid', {
          amount: formattedMaxGoalAmount,
        })
      )
    )
  }

  return {
    validate: combine(
      ...validators,
      required(
        'goalAmount',
        i18n('goal_amount_required_cf', { amount: formattedMinGoalAmount })
      ),
      atLeast(
        'goalAmount',
        MIN_AMOUNT || state.flags.min_amount_donation,
        i18n('goal_amount_invalid_min', {
          amount: formattedMinGoalAmount,
        })
      )
    ),
    initialValues: {
      poolName: campaign.title,
      goalAmount: campaign.goal ? campaign.goal : undefined,
      endDate: campaign.end_date ? new Date(campaign.end_date) : undefined,
      category: category,
      description: campaign.description,
      backgroundImage: campaign.background_image,
      backgroundPan: campaign.background_pan,
      backgroundImageFilename: campaign.background_image_filename,
      gradientColor: campaign.gradient_color,
    },
    ended: !campaign.can_chip_in,
    contribPrefs: stringifyContributionPrefs(campaign),
    tracking: stringifyPublicTrackingPrefs(campaign),
    currency: campaign.currency,
    showDelete: !campaign.preview,
    categoryName: getCategoryLabel(state, campaign.category),
    isAndroid:
      state.device_info.isMobile && state.device_info.deviceOS === 'Android',
    maxDays: CROWDFUNDING_CAMPAIGN_MAX_DAYS,
    maxGoalAmount: MAX_AMOUNT,
    categoryList: categoryList,
    campaign: campaign,
    charity: charity,
    formattedMaxGoalAmount: formattedMaxGoalAmount,
  }
}

const mapDispatchToProps = (dispatch, { campaignId }) => {
  return {
    onReopen() {
      dispatch(showComingSoon())
    },
    onSave(formValues) {
      return dispatch(
        updateRemotely(campaignId, {
          goal: formValues.goalAmount || 0,
          category: formValues.category,
          description: formValues.description,
          title: formValues.poolName,
          end_date: formValues.endDate,
          gradient_color: formValues.gradientColor,
          background_image: formValues.backgroundImage || '',
          background_image_filename: formValues.backgroundImageFilename,
          background_pan: formValues.backgroundPan,
        })
      )
    },
    onToggleToast(toastId) {
      dispatch(show(toastId))
    },
  }
}

export default reduxForm(
  {
    form: 'edit',
    fields: [
      'poolName',
      'goalAmount',
      'endDate',
      'category',
      'description',
      'backgroundImage',
      'backgroundImageFilename',
      'backgroundPan',
      'gradientColor',
    ],
  },
  mapStateToProps,
  mapDispatchToProps
)(formUtils(EditMain))
